import React, { useEffect, useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import {
  Button, Form, Radio, Select, Spin, Tooltip,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { toast } from "react-toastify";

import MyForm from "../../../MyForm";
import useHandleStepChange from "../../useHandleStepChange";
import BillingAddressSection from "../CompanyContactForm/Components/BillingAddressSection";
import ContactInfoSection from "../CompanyContactForm/Components/ContactInfoSection";
import EditableFields, {
  ENVIRONMENT_RESERVED_FIELD_KEYS,
  PRODUCT_RESERVED_FIELD_KEYS,
} from "./Components/EditableFields";
import FixedValueInput from "./Components/FixedValueInput";

import { getEnvironmentFields } from "../../../../../actions/envAnalytics";
import { getProductFields } from "../../../../../actions/reports";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../../Common/UIComponents/SelectPopupScrollbar";

import { ReactComponent as Info } from "../../../../../assets/images/onboarding/Info.svg";

import "../CompanyInfoForm.css";
import "../CompanyContactForm/CompanyContactForm.css";
import "../LinkReportForm.css";

export default function FieldsForm({
  initialValues, onNextButton, onBackButton, onFinishFailed, stepChangeInProgress,
  handleFormStepChange, existingUser, addCompany, companyName, submitting = false,
  contactInitialValues = { same_as_address: false },
  addressData,
  emailValidator,
  phoneValidator,
  managementType,
}) {
  const [formStep, setFormStep] = useState(1);
  const [productSampling, setProductSampling] = useState(null);
  const [environmentSampling, setEnvironmentSampling] = useState(null);
  const [productDropdownOptions, setProductDropdownOptions] = useState([]);
  const [envDropdownOptions, setEnvDropdownOptions] = useState([]);
  const [stepValues, setStepValues] = useState({ ...initialValues });
  const [nextButtonValid, setNextButtonValid] = useState(false);
  const [showEnvQuestion, setShowEnvQuestion] = useState(false);
  const [showProdQuestion, setShowProdQuestion] = useState(false);
  const [showProdQuesForm, setShowProdQuesForm] = useState(false);
  const [showEnvQuesForm, setShowEnvQuesForm] = useState(false);
  const [hasSelectedNoProd, setHasSelectedNoProd] = useState(false);
  const [hasSelectedNoEnv, setHasSelectedNoEnv] = useState(false);
  const [loading, setLoading] = useState(false);
  const FORM_NAME = "fields_form";
  const [form] = useForm();
  const [contactForm] = useForm();
  const layout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
    layout: "vertical",
  };

  /**
 * Filters the array of objects based on the "json_field" key and returns a new array with only the "json_field" and "title_field" keys, excluding the provided fields.
 *
 * @param {Array<{json_field, title_field}>} fields - The input array of objects of all the fields.
 * @param {Array<string>} excludeFields - The values to exclude in the "json_field" key, fields which are non editable.
 * @returns {Array<{json_field, title_field}>} A new array with only the "json_field" and "title_field" keys, excluding the provided fields, only editable fields are returned here.
 */
  function filterJsonField(fields, excludeFields) {
    return fields.filter((item) => !excludeFields.includes(item.json_field)).map(({ json_field, title_field }) => ({ json_field, title_field }));
  }

  /** Convert display name to DB Column value. Replace special characters, spaces with an underscore, convert to lower case */
  const getJsonFieldName = (str) => {
    if (!str) {
      return "";
    }
    let jsonFieldName = str.trim().replace(/[^\w\s]/gi, " ").replace(/\s+/g, "_").toLowerCase();
    jsonFieldName = jsonFieldName.replace(/(?:__+)/g, "_"); // condense multiple underscores to one
    jsonFieldName = jsonFieldName.replace(/^_+|_+$/g, ""); // trim leading + trailing underscores
    return jsonFieldName;
  };

  const PRODUCT_FIXED_FIELD_KEYS = ["sample_type", "date", "physical_category"];
  const ENV_FIXED_FIELD_KEYS = ["sample_type", "date", "physical_category", "swab_number", "zone", "section"];
  const ENV_LINKABLE_FIELD_KEYS = ["swab_number", "zone", "section"];
  const DEFAULT_PRODUCT_FIXED_FIELDS = {
    sample_type: "Sample Type",
    date: "Received Date",
    physical_category: "Physical Category",
  };
  const DEFAULT_PRODUCT_EDITABLE_FIELDS = [
    { json_field: "item", title_field: "Item" },
    { json_field: "lot", title_field: "Lot" },
    { json_field: "description", title_field: "Description" },
  ];
  const DEFAULT_ENV_FIXED_FIELDS = {
    sample_type: "Sample Type",
    date: "Received Date",
    physical_category: "Physical Category",
    swab_number: "Swab Number",
    zone: "Zone",
    section: "Section",
  };

  const definedFixedFieldTitles = (titles) => (
    Object.fromEntries(Object.entries(titles).filter(([, value]) => value != null && value !== ""))
  );

  const buildProdFixedFieldTitles = (fields = []) => {
    const fieldsMap = fields.reduce((acc, field) => {
      acc[field.json_field] = field.title_field;
      return acc;
    }, {});

    return definedFixedFieldTitles({
      sample_type: fieldsMap.sample_type,
      date: fieldsMap.received_date,
      physical_category: fieldsMap.physical_category,
    });
  };

  const buildEnvFixedFieldTitles = (fields = []) => {
    const fieldsMap = fields.reduce((acc, field) => {
      acc[field.json_field] = field.title_field;
      return acc;
    }, {});

    return definedFixedFieldTitles({
      sample_type: fieldsMap.sample_type,
      date: fieldsMap.received_date,
      physical_category: fieldsMap.physical_category,
      swab_number: fieldsMap.swab_number,
      zone: fieldsMap.zone,
      section: fieldsMap.section,
    });
  };

  const buildEnvDropdownOptions = (editableFields = [], fixedFieldTitles = {}) => {
    const fixedOptions = ENV_LINKABLE_FIELD_KEYS.map((key) => ({
      value: key,
      label: fixedFieldTitles[key],
    }));
    const editableOptions = (editableFields ?? []).map((item) => ({
      value: item.json_field,
      label: item.title_field,
    }));
    return [...fixedOptions, ...editableOptions];
  };

  const buildEnvDropdownOptionsFromApiFields = (fields = []) => {
    const nonLinkable = ["sample_type", "received_date", "physical_category", "env_add_company"];
    return fields
      .filter((field) => !nonLinkable.includes(field.json_field))
      .map((item) => ({ value: item.json_field, label: item.title_field }));
  };

  /**
   * "No" on same product details: show the default empty setup.
   * Does not call the company fields API.
   */
  const resetFieldsForProdForm = () => {
    const editableFields = DEFAULT_PRODUCT_EDITABLE_FIELDS;
    setProductDropdownOptions(editableFields.map((item) => ({
      value: item.json_field,
      label: item.title_field,
    })));
    form.setFieldsValue({
      sample_supply: "Yes",
      add_company: "No",
      ...DEFAULT_PRODUCT_FIXED_FIELDS,
      editable_fields: editableFields.map(({ title_field }) => title_field),
      product_link: [],
    });
    setShowProdQuesForm(true);
    setHasSelectedNoProd(true);
    setLoading(false);
    setNextButtonValid(false);
    setStepValues((prevState) => ({
      ...prevState,
      product_info: {
        ...prevState.product_info,
        ...DEFAULT_PRODUCT_FIXED_FIELDS,
        sample_supply: "Yes",
        add_company: "No",
        product_link: [],
        editable_fields: editableFields,
      },
    }));
  };
  /**
   * "No" on same environment details: show the default empty setup.
   * Does not call the company fields API.
   */
  const resetFieldsForEnvForm = () => {
    setEnvDropdownOptions(buildEnvDropdownOptions([], DEFAULT_ENV_FIXED_FIELDS));
    form.setFieldsValue({
      env_sample_supply: "Yes",
      env_add_company: "No",
      ...DEFAULT_ENV_FIXED_FIELDS,
      environment_link: [],
      editable_fields: [],
      environment_supplies_needed: "",
    });
    setShowEnvQuesForm(true);
    setHasSelectedNoEnv(true);
    setLoading(false);
    setNextButtonValid(false);
    setStepValues((prevState) => ({
      ...prevState,
      environment_info: {
        ...prevState.environment_info,
        ...DEFAULT_ENV_FIXED_FIELDS,
        env_sample_supply: "Yes",
        env_add_company: "No",
        environment_link: [],
        editable_fields: [],
        environment_supplies_needed: "",
      },
    }));
  };
  /**
   * if user wants to get field details from previous company for product
   * Set fields and other form data with received results
   */
  const getFieldsForProdForm = async () => {
    try {
      setLoading(true);
      const result = await getProductFields(true);
      const formData = form.getFieldsValue();
      const { link_report: linkReport, fields } = result;
      const fieldsMap = {};
      fields.forEach((fieldObj) => {
        fieldsMap[fieldObj.json_field] = fieldObj;
      });
      if (result && result.success) {
        if (formData.add_company === "Yes") {
          if (!linkReport || linkReport.length === 0) {
            toast.error("Link report field(s) missing");
          }
          const link_report_fields = linkReport.map((json_field) => fieldsMap[json_field]).filter((field) => field !== "");
          if (link_report_fields.some((elem) => !elem)) {
            toast.error("Link report field(s) missing from fields");
          }
          const nonEditables = ["sample_type", "received_date", "physical_category"];
          const editableFields = filterJsonField(result.fields, nonEditables);
          const fixedFieldTitles = buildProdFixedFieldTitles(result.fields);

          // Create and set dropdown options FIRST before setting values
          const options = editableFields.map((item) => ({ value: item.json_field, label: item.title_field }));
          setProductDropdownOptions(options);

          // Auto-fill the product_link with link_report values
          const validLinkReport = linkReport?.filter((elem) => elem !== "") ?? [];

          form.setFieldsValue({
            ...fixedFieldTitles,
            editable_fields: editableFields.map(({ title_field }) => title_field) ?? [],
          });

          // Set product_link after options are available
          setTimeout(() => {
            form.setFieldValue("product_link", validLinkReport);
          }, 0);

          setStepValues({
            ...stepValues,
            product_info: {
              ...stepValues.product_info,
              ...fixedFieldTitles,
              product_link: validLinkReport,
              editable_fields: editableFields,
            },
            environment_info: {
              ...stepValues.environment_info,
            },
          });
          setNextButtonValid(validLinkReport.length > 0);
          setShowProdQuesForm(true);
        }
        setLoading(false);
        return null;
      }
      if (hasSelectedNoProd) {
        form.setFieldValue("add_company", "No");
        resetFieldsForProdForm();
      } else {
        form.setFieldValue("add_company", "");
        setShowProdQuesForm(false);
      }
      toast.error("Could not fetch product fields!");
      setLoading(false);
      return null;
    } catch (error) {
      toast.error("Failed to fetch product fields");
      setLoading(false);
      return null;
    }
  };

  /**
   * if user wants to get field details from previous company for environments
   * Set fields and other form data with received results
   */
  const getFieldsForEnvForm = async () => {
    try {
      setLoading(true);
      const result = await getEnvironmentFields(true);
      const formData = form.getFieldsValue();
      if (result && result.success) {
        if (formData.env_add_company === "Yes") {
          const { fields, link_pattern_fields } = result;
          if (!link_pattern_fields || link_pattern_fields.length === 0) {
            toast.error("Link report field(s) missing");
          }
          const link_report_fields = link_pattern_fields.map((json_field) => fields.filter((field) => json_field === field.json_field));
          if (link_report_fields.some((elem) => !elem)) {
            toast.error("Link report field(s) missing from fields");
          }
          const nonEditables = ["sample_type", "received_date", "physical_category", "swab_number", "zone", "section", "env_add_company"];
          const editableFields = filterJsonField(result.fields, nonEditables);
          const fixedFieldTitles = buildEnvFixedFieldTitles(result.fields);
          const options = buildEnvDropdownOptionsFromApiFields(result.fields);
          setEnvDropdownOptions(options);
          const validLinkPatternFields = result.link_pattern_fields.filter((item) => item !== "") ?? [];
          form.setFieldsValue({
            ...fixedFieldTitles,
            editable_fields: editableFields.map(({ title_field }) => title_field) ?? [],
          });
          setTimeout(() => {
            form.setFieldValue("environment_link", validLinkPatternFields);
          }, 0);
          setStepValues({
            ...stepValues,
            environment_info: {
              ...stepValues.environment_info,
              ...fixedFieldTitles,
              environment_link: validLinkPatternFields,
              editable_fields: editableFields,
            },
          });
          setNextButtonValid(validLinkPatternFields.length > 0);
          setShowEnvQuesForm(true);
        }
        setLoading(false);
        return;
      }
      if (hasSelectedNoEnv) {
        form.setFieldValue("env_add_company", "No");
        resetFieldsForEnvForm();
      } else {
        form.setFieldValue("env_add_company", "");
        setShowEnvQuesForm(false);
      }
      toast.error("Could not fetch environment fields!");
      setLoading(false);
    } catch (error) {
      toast.error(error);
      setLoading(false);
    }
  };
  /**
   * @param {Array} fieldList
   * On change of fieldList in editableFields component
   * update the fields for product details and environment details.
   */
  const handleEditableFields = (fieldList) => {
    if (!Array.isArray(fieldList)) return;

    const currentStep = formStep === 1 ? "product_info" : "environment_info";
    const linkField = currentStep === "product_info" ? "product_link" : "environment_link";
    const previousFields = [...(stepValues[currentStep]?.editable_fields ?? [])];
    const seen = new Set();
    const editableOptions = [];
    const fieldCheck = fieldList.map((field) => {
      const rawTitle = typeof field === "string" ? field : "";
      const title = rawTitle.trim();
      const matchIndex = title
        ? previousFields.findIndex((previous) => (
          previous?.json_field && String(previous.title_field ?? "").trim() === title
        ))
        : -1;
      const jsonField = matchIndex >= 0
        ? previousFields.splice(matchIndex, 1)[0].json_field
        : getJsonFieldName(rawTitle);
      if (title && jsonField && !seen.has(jsonField)) {
        seen.add(jsonField);
        editableOptions.push({ value: jsonField, label: title });
      }
      return { json_field: jsonField, title_field: rawTitle };
    });

    const options = currentStep === "product_info"
      ? editableOptions
      : buildEnvDropdownOptions(
        editableOptions.map((option) => ({ json_field: option.value, title_field: option.label })),
        stepValues.environment_info,
      ).filter((option) => option.value && option.label);

    if (currentStep === "product_info") setProductDropdownOptions(options);
    else setEnvDropdownOptions(options);

    const valid = new Set(options.map((option) => option.value));
    const selected = [].concat(form.getFieldValue(linkField) ?? []);
    const nextSelected = selected.filter((value, index) => value && valid.has(value) && selected.indexOf(value) === index);
    if (nextSelected.length !== selected.length || nextSelected.some((value, index) => value !== selected[index])) {
      form.setFieldValue(linkField, nextSelected);
    }

    setStepValues({
      ...stepValues,
      [currentStep]: {
        ...stepValues[currentStep],
        editable_fields: fieldCheck,
        [linkField]: nextSelected,
      },
    });
  };

  /** Format values before saving */
  const formatValues = () => {
    const values = Object.keys(stepValues).length ? stepValues : initialValues;
    if (values.product_info?.editable_fields && values.environment_info?.editable_fields) {
      const valuesCopy = { ...values };
      const editableFieldsProduct = valuesCopy.product_info?.editable_fields ?? [];
      valuesCopy.product_info.editable_fields = editableFieldsProduct.map((displayName) => ({
        json_field: getJsonFieldName(displayName?.json_field ?? ""),
        title_field: displayName?.title_field?.trim() ?? "",
      }));
      const editableFieldsEnv = valuesCopy.environment_info?.editable_fields ?? [];
      valuesCopy.environment_info.editable_fields = editableFieldsEnv.map((displayName) => ({
        json_field: getJsonFieldName(displayName?.json_field ?? ""),
        title_field: displayName?.title_field?.trim() ?? "",
      }));
      if (addCompany) {
        const formData = form.getFieldsValue();
        if (formStep === 1) {
          valuesCopy.product_info.add_company = formData.add_company ?? "";
          valuesCopy.product_info.product_link = formData.product_link ?? [];
        } else {
          valuesCopy.environment_info.env_add_company = formData.env_add_company ?? "";
          valuesCopy.environment_info.environment_link = formData.environment_link ?? [];
          valuesCopy.environment_info.environment_supplies_needed = formData.environment_supplies_needed ?? "";
        }
      }
      return valuesCopy;
    }
    if (addCompany) {
      const formData = form.getFieldsValues();
      if (formStep === 1) {
        values.product_info.add_company = formData.add_company ?? "";
        values.product_info.product_link = formData.product_link ?? [];
      } else {
        values.environment_info.env_add_company = formData.env_add_company ?? "";
        values.environment_info.environment_link = formData.environment_link ?? [];
        values.environment_info.environment_supplies_needed = formData.environment_supplies_needed ?? "";
      }
    }
    return values;
  };

  const handleNextButton = (contactValues) => {
    let companyContact = contactValues;
    if (contactValues?.same_as_address && addressData) {
      companyContact = {
        ...contactValues,
        billing: {
          ...contactValues.billing,
          address: { ...addressData },
        },
      };
    }
    onNextButton(
      FORM_NAME,
      formatValues(),
      companyContact ? { company_contact_form: companyContact } : {},
    );
  };

  const handleBackButton = () => {
    onBackButton(FORM_NAME, formatValues());
  };

  /**
   * @param {String} formName
   * Handling the initialValues for form
   */
  const handleInitialValues = (formName) => {
    if (formName && Object.keys(stepValues).length) {
      const editable_fields = stepValues[formName].editable_fields ? stepValues[formName].editable_fields.map(({ title_field }) => title_field) : undefined;
      return { ...stepValues[formName], editable_fields };
    }
    return {};
  };

  /**
   * Helper function to restore product form values from stepValues
   * Centralizes the logic for better maintainability
   * @param {boolean} restoreVisibility - Whether to restore visibility states (only when navigating back)
   */
  const restoreProductFormValues = (restoreVisibility = false) => {
    if (!stepValues?.product_info || Object.keys(stepValues.product_info).length === 0) {
      return;
    }

    const { product_info } = stepValues;

    // Ensure dropdown options are available
    if (productDropdownOptions.length === 0 && product_info.editable_fields?.length > 0) {
      const options = product_info.editable_fields.map((item) => ({
        value: item.json_field,
        label: item.title_field,
      }));
      setProductDropdownOptions(options);
    }

    // Build fields to set
    const fieldsToSet = PRODUCT_FIXED_FIELD_KEYS.reduce((acc, key) => {
      acc[key] = product_info[key];
      return acc;
    }, {
      editable_fields: product_info.editable_fields
        ? product_info.editable_fields.map((data) => data.title_field)
        : [],
    });

    if (product_info.sample_supply) {
      fieldsToSet.sample_supply = product_info.sample_supply;
    }

    if (addCompany && product_info.add_company !== undefined) {
      fieldsToSet.add_company = product_info.add_company ?? "";
    }

    // Set all fields at once
    form.setFieldsValue(fieldsToSet);

    // Set product_link with setTimeout to ensure dropdown options are rendered (Ant Design requirement)
    if (product_info.product_link !== undefined) {
      setTimeout(() => {
        form.setFieldValue("product_link", product_info.product_link ?? []);
      }, 0);
    }

    // Only restore visibility state variables when explicitly requested (e.g., when navigating back)
    // This prevents overriding user interactions during active form filling
    if (restoreVisibility && addCompany && product_info.sample_supply === "Yes") {
      setShowProdQuestion(true);
      if (product_info.add_company !== undefined && product_info.add_company !== "") {
        setShowProdQuesForm(true);
        if (product_info.add_company === "No") {
          setHasSelectedNoProd(true);
        }
      }
    }
  };

  /**
   * Helper function to restore environment form values from stepValues
   * Centralizes the logic for better maintainability
   */
  const restoreEnvironmentFormValues = () => {
    if (!Object.keys(stepValues).length) {
      return;
    }

    const { environment_info } = stepValues;

    // Build fields to set
    const envFieldsToSet = ENV_FIXED_FIELD_KEYS.reduce((acc, key) => {
      acc[key] = environment_info[key];
      return acc;
    }, {
      editable_fields: environment_info.editable_fields?.map((data) => data.title_field) ?? [],
    });

    if (environment_info.environment_link !== undefined) {
      envFieldsToSet.environment_link = environment_info.environment_link ?? [];
    }

    if (environment_info.env_sample_supply !== undefined) {
      envFieldsToSet.env_sample_supply = environment_info.env_sample_supply;
    }

    if (environment_info.environment_supplies_needed !== undefined) {
      envFieldsToSet.environment_supplies_needed = environment_info.environment_supplies_needed;
    }

    if (addCompany && environment_info.env_add_company !== undefined) {
      envFieldsToSet.env_add_company = environment_info.env_add_company ?? "";
    }

    form.setFieldsValue(envFieldsToSet);

    // Initialize dropdown options if needed
    if (envDropdownOptions.length === 0) {
      setEnvDropdownOptions(buildEnvDropdownOptions(environment_info.editable_fields, environment_info));
    }

    if (environment_info.environment_link !== undefined) {
      setTimeout(() => {
        form.setFieldValue("environment_link", environment_info.environment_link ?? []);
      }, 0);
    }
  };

  /**
   * Helper function to validate and set next button state for product form
   */
  const validateProductForm = (formData) => {
    if (productSampling && stepValues?.product_info.product_link?.length === 0) {
      setNextButtonValid(false);
    } else if (formData.sample_supply === "" || formData.sample_supply === null) {
      setNextButtonValid(false);
    } else {
      setNextButtonValid(true);
    }
    if (productSampling && formData?.product_info?.product_link?.length !== 0) {
      setNextButtonValid(true);
    }
  };

  /**
   * Helper function to validate and set next button state for environment form
   */
  const validateEnvironmentForm = (formData) => {
    if (
      (environmentSampling && formData?.environment_link?.length === 0)
      || (environmentSampling && formData?.environment_link === undefined)
    ) {
      setNextButtonValid(false);
    } else if (formData.env_sample_supply === "" || formData.env_sample_supply === null) {
      setNextButtonValid(false);
    } else {
      setNextButtonValid(true);
    }
    if (addCompany && formData?.environment_info?.environment_link?.length !== 0) {
      setNextButtonValid(true);
    }
    if (environmentSampling && formData?.environment_info?.environment_link?.length !== 0) {
      setNextButtonValid(true);
    }
  };

  /**
   * @param {*} e
   * Handling the product form data for next button from
   * product details to environment details page
   */
  const handleFormStepNext = (e) => {
    e.preventDefault();
    const formData = form.getFieldsValue();
    const fieldsErrors = form.getFieldsError();
    const hasErrors = fieldsErrors.some((field) => field.errors.length > 0);
    if (hasErrors) {
      form.validateFields();
      toast.error("Invalid inputs!");
      return;
    }
    if (formStep === 1) {
      const fieldData = stepValues.product_info.editable_fields.filter((field) => field.json_field === "");
      if (addCompany && formData?.add_company === "") {
        form.validateFields();
        toast.error("Invalid inputs!");
        return;
      }
      if (formData?.product_link && formData?.sample_supply === "Yes") {
        if (!formData.product_link.length) {
          form.validateFields(["product_link"]);
          toast.error("At least one field required!");
          return;
        }
        if (fieldData?.length > 0) {
          form.validateFields();
          toast.error("Invalid inputs!");
          return;
        }
      }
      if (formData?.sample_supply === "Yes") {
        if (formData?.editable_fields?.length < 1) {
          form.validateFields();
          toast.error("At least one field required!");
          return;
        }
      }

      form.setFieldValue("editable_fields", stepValues.environment_info.editable_fields.map(({ title_field }) => title_field) ?? []);
      setStepValues({
        ...stepValues,
        product_info: {
          ...stepValues.product_info,
          sample_supply: formData.sample_supply,
          product_link: formData.product_link ?? [],
          add_company: formData.add_company ?? "",
          // editable_fields: formData.editable_fields ?? [],
        },
        environment_info: {
          ...stepValues.environment_info,
        },
      });
      setFormStep(2);
    } else if (formStep === 2) {
      const fieldData = stepValues.environment_info.editable_fields.filter((field) => field?.json_field === "");
      if (addCompany && formData?.env_add_company === "") {
        form.validateFields();
        toast.error("Invalid inputs!");
        return;
      }
      if (fieldData.length > 0) {
        form.validateFields();
        toast.error("Invalid inputs!");
        return;
      }
      if (formData?.environment_link && formData?.env_sample_supply === "Yes") {
        if (!formData.environment_link.length) {
          form.validateFields(["environment_link"]);
          toast.error("At least one field required!");
          return;
        }
        if (fieldData?.length > 0) {
          form.validateFields();
          toast.error("Invalid inputs!");
          return;
        }
      }
      if (formData.environment_supplies_needed === "" && formData.env_sample_supply === "Yes") {
        form.validateFields(["environment_supplies_needed"]);
        toast.error("Invalid input");
        return;
      }
      if (!productSampling && !environmentSampling) {
        form.validateFields();
        toast.error("Please provide Product or Environment details");
        return;
      }
      stepValues.product_info.sample_supply = productSampling ? "Yes" : "No";
      stepValues.environment_info.env_sample_supply = environmentSampling ? "Yes" : "No";
      stepValues.environment_info.environment_link = formData.environment_link ?? [];
      stepValues.environment_info.environment_supplies_needed = formData.environment_supplies_needed ?? "No";
      setStepValues({
        ...stepValues,
        product_info: { ...stepValues.product_info },
        environment_info: {
          ...stepValues.environment_info,
          editable_fields: stepValues.environment_info.editable_fields ?? [],
          environment_link: formData.environment_link ?? [],
          environment_supplies_needed: formData.environment_supplies_needed ?? "",
          env_sample_supply: formData.env_sample_supply,
          env_add_company: formData.env_add_company ?? "",
        },
      });
      setFormStep(3);
      setNextButtonValid(true);
    } else if (formStep === 3) {
      contactForm.validateFields()
        .then((contactValues) => {
          handleNextButton(contactValues);
        })
        .catch(() => {
          toast.error("Invalid input");
        });
    }
  };

  /**
   * @param {*} e
   * Handling the product form data for back button from
   * environment details to product details page
   */
  const handleFormStepBack = (e) => {
    e.preventDefault();
    const formData = form.getFieldsValue();

    if (formStep === 3) {
      setFormStep(2);
      restoreEnvironmentFormValues();
    } else if (formStep === 2) {
      // Save environment form data before going back to product step
      setStepValues({
        ...stepValues,
        product_info: { ...stepValues.product_info },
        environment_info: {
          ...stepValues.environment_info,
          editable_fields: stepValues.environment_info.editable_fields ?? [],
          environment_link: formData.environment_link ?? [],
          environment_supplies_needed: formData.environment_supplies_needed ?? "",
          env_sample_supply: formData.env_sample_supply,
          env_add_company: formData.env_add_company ?? "",
        },
      });

      setFormStep(1);
      // Restore product form values with visibility states when navigating back
      restoreProductFormValues(true);
    } else {
      // Save product form data before going to previous step
      setStepValues({
        ...stepValues,
        product_info: {
          ...stepValues.product_info,
          sample_supply: productSampling ? "Yes" : "No",
          product_link: formData.product_link,
          add_company: formData.add_company ?? "",
        },
        environment_info: {
          ...stepValues.environment_info,
          env_sample_supply: environmentSampling ? "Yes" : "No",
        },
      });
      handleBackButton();
    }
  };

  /**
   * Handling the product form data onload
   */
  useEffect(() => {
    if (initialValues.product_info.sample_supply) {
      setProductSampling(initialValues.product_info.sample_supply === "Yes");
      if (addCompany) {
        setShowProdQuestion(initialValues.product_info.sample_supply === "Yes");
        setShowProdQuesForm(initialValues.product_info.add_company !== "");
      }
    }
    if (initialValues.environment_info.env_sample_supply) {
      setEnvironmentSampling(initialValues.environment_info.env_sample_supply === "Yes");
      if (addCompany) {
        setShowEnvQuestion(initialValues.environment_info.env_sample_supply === "Yes");
        setShowEnvQuesForm(initialValues.environment_info.env_add_company !== "");
      }
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @dependency [formStep]
   * Handling the form data on step change
   * Simplified using helper functions for better maintainability
   * Only restores when step changes, not on every stepValues change to avoid interfering with user interactions
   */
  useEffect(() => {
    const formData = form.getFieldsValue();

    if (formStep === 1) {
      // Validate product form
      validateProductForm(formData);
      // Restore product form values with visibility states when step changes
      restoreProductFormValues(true);
    } else if (formStep === 2) {
      // Validate environment form
      validateEnvironmentForm(formData);
      // Restore environment form values
      restoreEnvironmentFormValues();
    } else if (formStep === 3) {
      setNextButtonValid(true);
    }
  }, [formStep]);// eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @dependency [stepValues, formStep]
   * Validate form when stepValues change (but don't restore to avoid interfering with user interactions)
   */
  useEffect(() => {
    const formData = form.getFieldsValue();
    if (formStep === 1) {
      validateProductForm(formData);
    } else if (formStep === 2) {
      validateEnvironmentForm(formData);
    }
  }, [stepValues, formStep]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    // const fieldsData = stepValues;
    const formData = form.getFieldsValue();
    if (formStep === 2) {
      setStepValues({
        ...stepValues,
        product_info: { ...stepValues.product_info },
        environment_info: {
          ...stepValues.environment_info,
          editable_fields: stepValues.environment_info.editable_fields ?? [],
          environment_link: formData.environment_link ?? [],
          environment_supplies_needed: formData.environment_supplies_needed ?? "",
          env_sample_supply: formData.env_sample_supply,
          env_add_company: formData.env_add_company ?? "",
        },
      });
    } else {
      setStepValues({
        ...stepValues,
        product_info: {
          ...stepValues.product_info,
          sample_supply: formData.sample_supply,
          product_link: formData.product_link,
          add_company: formData.add_company,
          editable_fields: stepValues.product_info.editable_fields ?? [],
        },
        environment_info: { ...stepValues.environment_info },
      });
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  /** Hook that handles form saving + step change when step is clicked */
  useHandleStepChange(FORM_NAME, form, stepChangeInProgress, handleFormStepChange, formatValues);

  return (
    <div className="FormsContainer">
      {formStep === 1 && (
      <MyForm
        {...layout}// eslint-disable-line react/jsx-props-no-spreading
        form={form}
        requiredMark={false}
        name={FORM_NAME}
        validateTrigger={["onChange", "onBlur"]}
        initialValues={handleInitialValues("product_info")}
        onFinish={handleNextButton}
        onFinishFailed={onFinishFailed}
        className="OnboardingForm"
      >
        {/* Product Details Form */}
        <div className="productDetail">
          <div className="OnboardingFormTitle">Product Details</div>
          <Form.Item
            name="sample_supply"
            label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">Are you planning to send us product samples for testing?</span>}
            rules={[
              {
                required: true, message: "",
              },
            ]}
          >
            <Radio.Group
              className="Onboarding__RadioGroup"
              onChange={(e) => {
                form.setFieldValue("sample_supply", e.target.value);
                setProductSampling(e.target.value === "Yes");
                setStepValues({ product_info: { ...stepValues.product_info, sample_supply: e.target.value }, environment_info: { ...stepValues.environment_info } });
                if (e.target.value === "Yes") {
                  setShowProdQuestion(true);
                } else {
                  setShowProdQuestion(false);
                  setShowProdQuesForm(false);
                  setHasSelectedNoProd(false);
                  setLoading(false);
                  form.setFieldValue("add_company", "");
                }
              }}
            >
              <div className="Onboarding__RadioOption">
                <Radio value="Yes">Yes</Radio>
              </div>
              <div className="Onboarding__RadioOption">
                <Radio value="No">No</Radio>
              </div>
            </Radio.Group>
          </Form.Item>

          { addCompany && showProdQuestion
          && (
          <Form.Item
            name="add_company"
            label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">{`Do you want to use the same product details as ${companyName} (Makes things faster!)?`}</span>}
            rules={[
              {
                required: true, message: "Please provide details for the product",
              },
            ]}
          >
            <Radio.Group
              className="Onboarding__RadioGroup"
              onChange={(e) => {
                form.setFieldValue("add_company", e.target.value);
                if (e.target.value === "Yes") {
                  getFieldsForProdForm();
                } else {
                  resetFieldsForProdForm();
                }
              }}
            >
              {
                loading ? (
                  <div className="FieldForm__Loading">
                    <Spin indicator={<LoadingOutlined className="FieldForm__Checkbox__Spin" spin />} />
                    <span className="FieldForm_Radio_label">Yes</span>
                    <div className="Onboarding__RadioOption">
                      <Radio value="No" disabled={loading}>No</Radio>
                    </div>
                  </div>
                )
                  : (
                    <>
                      <div className="Onboarding__RadioOption">
                        <Radio value="Yes">Yes</Radio>
                      </div>
                      <div className="Onboarding__RadioOption">
                        <Radio value="No">No</Radio>
                      </div>
                    </>
                  )
              }
            </Radio.Group>
          </Form.Item>
          )}

          {(addCompany ? showProdQuesForm : productSampling)
                  && (
                    <div>
                      <div className="FormsContainer_Divider" />
                      <Form.Item label={(
                        <span className="MyForm__SectionLabel FieldsForm__LabelText_Fields">
                          Product Sample Submission Form Fields
                          <Tooltip title="Info to track the samples you send to the lab.">
                            <Info />
                          </Tooltip>
                        </span>
                        )}
                      >
                          <Form.Item
                            name="sample_type"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="date"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="physical_category"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <EditableFields getJsonFieldName={getJsonFieldName} onGetData={handleEditableFields} removableFieldIndex={2} reservedFieldKeys={PRODUCT_RESERVED_FIELD_KEYS} />
                          <div className="FormsContainer_Divider" />
                          <Form.Item
                            name="product_link"
                            label={<span className="MyForm__SectionLabel">Select the field that is associated with the specification for your product :</span>}
                            rules={[
                              {
                                required: true, message: "",
                              },
                            ]}
                            className="LinkReportForm__ProductSpec"
                          >
                            <Select
                              mode="multiple"
                              // disabled={prodDetailsCopy}
                              allowClear
                              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                              classNames={selectPopupClassNames()}
                              virtual={false}
                              listHeight={256}
                              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                                optionCount: (productDropdownOptions || []).length,
                              })}
                              options={productDropdownOptions}
                              placeholder="Item Name"
                              onChange={() => {
                                const fieldValue = form.getFieldValue("product_link");
                                const editableFieldsValue = form.getFieldValue("editable_fields") ?? [];
                                if (fieldValue.length === 0 || editableFieldsValue.some((field) => !String(field ?? "").trim())) {
                                  setNextButtonValid(false);
                                } else {
                                  setNextButtonValid(true);
                                }
                              }}
                            />
                          </Form.Item>
                      </Form.Item>
                    </div>
                  )}
        </div>
      </MyForm>// eslint-disable-next-line func-call-spacing
      )}
      {formStep === 2
        && (
        <MyForm
          {...layout}// eslint-disable-line react/jsx-props-no-spreading
          form={form}
          requiredMark={false}
          name={FORM_NAME}
          validateTrigger={["onChange", "onBlur"]}
          initialValues={handleInitialValues("environment_info")}
          onFinish={handleNextButton}
          onFinishFailed={onFinishFailed}
          className="OnboardingForm"
        >
          {/* Environment Details Form */}
          <div className="productDetail">
            <div className="OnboardingFormTitle">Environment Details</div>
            <Form.Item>
              <Form.Item
                name="env_sample_supply"
                label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">Are you planning to send us environment samples for testing?</span>}
                rules={[
                  {
                    required: true, message: "",
                  },
                ]}
              >
                <Radio.Group
                  className="Onboarding__RadioGroup"
                  onChange={(e) => {
                    form.setFieldValue("env_sample_supply", e.target.value);
                    setEnvironmentSampling(e.target.value === "Yes");
                    setStepValues({ product_info: { ...stepValues.product_info }, environment_info: { ...stepValues.environment_info, env_sample_supply: e.target.value } });
                    if (e.target.value === "Yes") {
                      setShowEnvQuestion(true);
                    } else {
                      form.setFieldValue("env_add_company", "");
                      setHasSelectedNoEnv(false);
                      setShowEnvQuestion(false);
                      setLoading(false);
                      setShowEnvQuesForm(false);
                    }
                  }}
                >
                  <div className="Onboarding__RadioOption">
                    <Radio value="Yes">Yes</Radio>
                  </div>
                  <div className="Onboarding__RadioOption">
                    <Radio value="No">No</Radio>
                  </div>
                </Radio.Group>
              </Form.Item>
              { addCompany && showEnvQuestion
                && (
                <Form.Item
                  name="env_add_company"
                  label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">{`Do you want to use the same environment details as ${companyName} (Makes things faster!)?`}</span>}
                  rules={[
                    {
                      required: true, message: "Please provide the details for the environment",
                    },
                  ]}
                >
                  <Radio.Group
                    className="Onboarding__RadioGroup"
                    onChange={(e) => {
                      form.setFieldValue("env_add_company", e.target.value);
                      if (e.target.value === "Yes") {
                        getFieldsForEnvForm();
                      } else {
                        resetFieldsForEnvForm();
                      }
                    }}
                  >
                    {
                      loading ? (
                        <div className="FieldForm__Loading">
                          <Spin indicator={<LoadingOutlined className="FieldForm__Checkbox__Spin" spin />} />
                          <span className="FieldForm_Radio_label">Yes</span>
                          <div className="Onboarding__RadioOption">
                            <Radio value="No" disabled={loading}>No</Radio>
                          </div>
                        </div>
                      )
                        : (
                          <>
                            <div className="Onboarding__RadioOption">
                              <Radio value="Yes">Yes</Radio>
                            </div>
                            <div className="Onboarding__RadioOption">
                              <Radio value="No">No</Radio>
                            </div>
                          </>
                        )
              }
                  </Radio.Group>
                </Form.Item>
                )}
              {(addCompany ? showEnvQuesForm : environmentSampling)
                  && (
                    <div>
                      <div className="" />
                      <Form.Item label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Please enter the details you would like to include while submitting Environment samples</span>}>
                        <Form.Item label={(
                          <span className="MyForm__SectionLabel FieldsForm__LabelText_Fields">
                            Environment Sample Submission Form Fields
                            <Tooltip title="Info to track the samples you send to the lab.">
                              <Info />
                            </Tooltip>
                          </span>
                          )}
                        >
                          <Form.Item
                            name="sample_type"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="date"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="physical_category"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="swab_number"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="zone"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <Form.Item
                            name="section"
                            rules={[
                              {
                                required: true,
                                message: "",
                              },
                            ]}
                          >
                            <FixedValueInput />
                          </Form.Item>
                          <EditableFields getJsonFieldName={getJsonFieldName} onGetData={handleEditableFields} fieldLimit={3} reservedFieldKeys={ENVIRONMENT_RESERVED_FIELD_KEYS} />
                          <div className="FormsContainer_Divider" />
                          <Form.Item
                            name="environment_link"
                            rules={[
                              {
                                required: true, message: "",
                              },
                            ]}
                            label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Please select the field(s) associated with the specification for your environment:</span>}
                          >
                            <Select
                              mode="multiple"
                              // disabled={envDetailsCopy}
                              loading={loading}
                              allowClear
                              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                              classNames={selectPopupClassNames()}
                              virtual={false}
                              listHeight={256}
                              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                                optionCount: (envDropdownOptions || []).length,
                              })}
                              options={envDropdownOptions}
                              placeholder="Select field(s)"
                              onChange={() => {
                                const fieldValue = form.getFieldValue("environment_link");
                                const editableFieldsValue = form.getFieldValue("editable_fields") ?? [];
                                if (fieldValue.length === 0 || editableFieldsValue.some((field) => !String(field ?? "").trim())) {
                                  setNextButtonValid(false);
                                } else {
                                  setNextButtonValid(true);
                                }
                              }}
                            />
                          </Form.Item>
                          {!existingUser && <div className="FormsContainer_Divider" />}
                          {!existingUser && (
                          <Form.Item
                            name="environment_supplies_needed"
                            label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Do you need supplies for environment testing?</span>}
                            rules={[
                              {
                                required: true, message: "Please provide an answer",
                              },
                            ]}
                          >
                            <Radio.Group className="Onboarding__RadioGroup" disabled={loading}>
                              <div className="Onboarding__RadioOption">
                                <Radio value="Yes">Yes</Radio>
                              </div>
                              <div className="Onboarding__RadioOption">
                                <Radio value="No">No</Radio>
                              </div>
                            </Radio.Group>
                          </Form.Item>
                          )}
                        </Form.Item>
                      </Form.Item>
                    </div>
                  )}
            </Form.Item>
          </div>
        </MyForm>
        )}
      {formStep === 3 && (
        <MyForm
          {...layout}// eslint-disable-line react/jsx-props-no-spreading
          form={contactForm}
          requiredMark={false}
          name="company_contact_form"
          validateTrigger={["onBlur", "onChange"]}
          initialValues={contactInitialValues}
          onFinishFailed={onFinishFailed}
          className="OnboardingForm"
        >
          <BillingAddressSection
            initialValues={contactInitialValues}
            emailValidator={emailValidator}
            phoneValidator={phoneValidator}
            addressData={addressData}
            contactSection={(
              <ContactInfoSection
                emailValidator={emailValidator}
                phoneValidator={phoneValidator}
              />
            )}
          />
        </MyForm>
      )}
      <div className="FormButtons">
        <Button
          type="default"
          onClick={handleFormStepBack}
        >
          {formStep === 3 ? "Cancel" : "Previous"}
        </Button>
        <Button
          type="primary"
          htmlType="button"
          onClick={handleFormStepNext}
          disabled={!nextButtonValid}
          loading={submitting || false}
        >
          {formStep === 3 ? "Save" : `Next ${formStep}/2`}
        </Button>
      </div>
    </div>

  );
}
